import axios from "axios";
import {
  TBasketPackageViewModel,
  TBasketUpdateRequest,
  TFixtureLabels,
  TPackageDate,
  TPackageOptionalExtra,
  TPackageRoom,
} from "components/Fixtures/Fixtures.types";
import { useEffect, useMemo, useState } from "react";
import { Loader, Select } from "shared";
import { TOption as TOptionBase } from "shared/Select/Select";
import DOMPurify from "dompurify";

type TPackageRoomProps = {
  packageRoom: TPackageRoom;
  renderPrice: (price: number, fractionDigitsCount?: number) => string;
  labels: TFixtureLabels;
  domain: string;
  facilityName: string;
};

const renderQuantity = (
  quantity: number,
  onChange: (quantity: number) => void
) => (
  <div className="o-fixture-package__quantity-selection">
    <button
      className="o-fixture-package__action-btn"
      onClick={() => onChange(quantity - 1)}
      disabled={quantity === 0}
    >
      <span>-</span>
    </button>
    <span className={quantity === 0 ? "disabled" : ""}>{quantity}</span>
    <button
      className="o-fixture-package__action-btn"
      onClick={() => onChange(quantity + 1)}
    >
      <span>+</span>
    </button>
  </div>
);

type TOption = TOptionBase & {
  packageName: string;
  value: number;
  packageQuantity: number;
  venueName: string;
  tagText: string;
  percent: number;
  showTBCDate: boolean;
};

function PackageRoom({
  packageRoom,
  renderPrice,
  labels,
  domain,
  facilityName,
}: TPackageRoomProps) {
  const [isOptionsLoading, setIsOptionsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isBasketSubmitting, setIsBasketSubmitting] = useState(false);
  const [isBasketSuccess, setIsBasketSucccess] = useState(false);
  const [isBasketError, setIsBasketError] = useState(false);
  const [isBasketEmptyError, setIsBasketEmptyError] = useState(false);
  const [optionValue, setOptionValue] = useState<TOption | undefined>();

  const [packageOptions, setPackageOptions] = useState<
    TPackageDate[] | undefined
  >();
  const [optionalExtras, setOptionalExtras] = useState<
    TPackageOptionalExtra[] | undefined
  >();
  const [isVATExcludedPrice, setIsVatExcludedPrice] = useState<boolean>(false);
  const getActiveTagText = (percent?: number) => {
    if (percent === 0) {
      return labels.soldOutText;
    }

    if (!percent) {
      return "";
    }

    if (percent < 20) {
      return labels.limitedAvailabilityText;
    }

    if (percent <= 39) {
      return labels.sellingFast;
    }

    return "";
  };

  const selectOptions: TOption[] = useMemo(() => {
    return packageRoom.PackageDetails.map((packageDetail) => {
      const optionTagText =
        Boolean(packageDetail.AvailabilityText) &&
        Number(packageDetail.AvailabilityText?.length) > 0
          ? packageDetail.AvailabilityText
          : getActiveTagText(packageDetail.AvailabilityPercent);
      const isSoldOut =
        optionTagText && optionTagText?.toLowerCase().includes("sold");

      return {
        label: `${packageDetail.NumberOfGuests}${
          isSoldOut ? " - " + labels.enquireSelectText : ""
        }`,
        value: packageDetail.PackageId,
        packageName: packageDetail.PackageName,
        packageQuantity: packageDetail.NumberOfGuests,
        venueName: packageDetail.VenueName,
        tagText: packageDetail.AvailabilityText ?? "",
        percent: packageDetail.AvailabilityPercent,
        showTBCDate: packageDetail.ShowTBCDate,
      };
    });
    // eslint-disable-next-line
  }, [packageRoom.PackageDetails, labels]);

  const activeOption = selectOptions.find(
    (opt) => opt.value === optionValue?.value
  );

  const tagText =
    Boolean(activeOption?.tagText) && Number(activeOption?.tagText?.length) > 0
      ? activeOption?.tagText
      : getActiveTagText(activeOption?.percent);

  const getConfigBasedOnTagText = (text: string) => {
    if (text.toLowerCase().includes("sold")) {
      return {
        icon: "close",
        className: "",
      };
    }

    if (text.toLowerCase().includes("limited")) {
      return {
        icon: "ticket-star",
        className: "o-fixture-package__tag--blue",
      };
    }

    if (text.toLowerCase().includes("selling")) {
      return {
        icon: "hourglass-alt",
        className: "o-fixture-package__tag--dark",
      };
    }

    return {
      icon: "star",
      className: "o-fixture-package__tag--green",
    };
  };

  const fetchPackageInfos = (optionValue: TOption | undefined) => {
    if (!isExpanded || !Boolean(optionValue)) {
      return;
    }

    setIsOptionsLoading(true);
    axios
      .get(
        domain +
          `/api/sitecore/fixturebasket/getpackageinfo?packageId=${optionValue?.value}&packageName=${optionValue?.packageName}`,
        {
          withCredentials: true,
        }
      )
      .then((res) => {
        setIsOptionsLoading(false);

        if (res?.data) {
          const result = res.data as TBasketPackageViewModel;

          const packageCounts = result.PackageDates.length;

          if (
            packageCounts === 1 &&
            !(result.PackageDates[0].OutOfStock || isSoldOutPackage)
          ) {
            setPackageOptions([{ ...result.PackageDates[0], Quantity: 1 }]);
          } else {
            setPackageOptions(result.PackageDates);
          }

          setOptionalExtras(result.OptionalExtras);
          setIsVatExcludedPrice(!result.Basket.PriceIncludesVAT);
        }
      })
      .catch(() => {
        setIsOptionsLoading(false);
      });
  };

  const addToBasket = () => {
    if (totalPrice === 0) {
      setIsBasketEmptyError(true);

      setTimeout(() => setIsBasketEmptyError(false), 10000);
      return;
    }

    setIsBasketSubmitting(true);

    if (!optionalExtras || !packageOptions || !optionValue) {
      return;
    }

    const data: TBasketUpdateRequest = {
      EventId: Number(labels.eventId),
      EventCategory: labels.eventCategory,
      EventName: labels.eventName,
      VenueName: optionValue.venueName,
      FacilityName: facilityName,
      PackageId: optionValue.value,
      PackageName: optionValue.packageName,
      OptionalExtras: optionalExtras.filter((item) => Boolean(item.Quantity)),
      PackageDates: packageOptions.filter((item) => Boolean(item.Quantity)),
    };

    axios
      .request({
        data,
        method: "POST",
        url: domain + "/api/sitecore/fixturebasket/updatebasket",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        withCredentials: true,
      })
      .then((res) => {
        if (res?.data?.TotalItems) {
          setIsBasketSucccess(true);

          setIsExpanded(false);
          setTimeout(() => setIsBasketSucccess(false), 3000);

          const basketHeaderWrapper =
            document.getElementsByClassName("icon-basket");
          if (basketHeaderWrapper && basketHeaderWrapper[0]) {
            if (!basketHeaderWrapper[0].classList.contains("active")) {
              basketHeaderWrapper[0].classList.add("active");
            }
          }
          const basketHeaderElem = document.querySelectorAll(
            '[data-basketproperty="TotalItemsCount"]'
          );
          if (basketHeaderElem) {
            basketHeaderElem.forEach((elem) => {
              elem.textContent = res?.data?.TotalItems;
            });
          }
        } else {
          setIsBasketError(true);

          setTimeout(() => setIsBasketError(false), 10000);
        }

        setIsBasketSubmitting(false);
      })
      .catch(() => {
        setIsBasketSubmitting(false);

        setIsBasketError(true);

        setTimeout(() => setIsBasketError(false), 10000);
      });
  };

  useEffect(() => {
    if (isExpanded) {
      fetchPackageInfos(optionValue);
    } else {
      setPackageOptions(undefined);
      setOptionalExtras(undefined);
    }
    // eslint-disable-next-line
  }, [isExpanded, optionValue]);

  useEffect(() => {
    const notSoldOutOption =
      selectOptions?.find((option) => {
        const optionTagText =
          Boolean(option?.tagText) && Number(option?.tagText?.length) > 0
            ? option?.tagText
            : getActiveTagText(option?.percent);

        return !(optionTagText && optionTagText.toLowerCase().includes("sold"));
      }) ?? selectOptions?.[0];

    setOptionValue(notSoldOutOption);
    // eslint-disable-next-line
  }, [selectOptions]);

  const totalPrice = useMemo(() => {
    if (!optionValue) {
      return 0;
    }

    const packageOptionTotal =
      packageOptions?.reduce(
        (acc, item) =>
          acc +
          item.Quantity *
            (isVATExcludedPrice ? item.PriceExcVat : item.PriceIncVat),
        0
      ) ?? 0;
    const optionalExtrasTotal =
      optionalExtras?.reduce(
        (acc, item) =>
          acc +
          item.Quantity *
            (isVATExcludedPrice ? item.PriceExcVat : item.PriceIncVat),
        0
      ) ?? 0;

    return packageOptionTotal + optionalExtrasTotal;
  }, [optionalExtras, packageOptions, isVATExcludedPrice, optionValue]);

  useEffect(() => {
    if (isBasketSuccess) {
      document
        .getElementById("fixture-basket-success")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isBasketSuccess]);

  const isSoldOutPackage = tagText && tagText.toLowerCase().includes("sold");

  const isSoldOutRoomOptions =
    packageRoom.IsSoldOut ||
    !selectOptions.some((option) => {
      const optionTagText =
        Boolean(option?.tagText) && Number(option?.tagText?.length) > 0
          ? option?.tagText
          : getActiveTagText(option?.percent);

      return !(optionTagText && optionTagText.toLowerCase().includes("sold"));
    });

  return (
    <div className="o-fixture-package">
      {tagText && tagText?.length > 0 ? (
        <div
          className={`o-fixture-package__tag ${
            getConfigBasedOnTagText(tagText)?.className
          }`}
        >
          <span>{tagText}</span>
          <i
            className={`zmdi zmdi-${getConfigBasedOnTagText(tagText)?.icon}`}
          ></i>
        </div>
      ) : null}

      <div className="o-fixture-package__heading">
        <h4>{packageRoom.Title}</h4>
        <div className="o-fixture-package__heading-group">
          <i className="zmdi zmdi-accounts"></i>
          <span>{packageRoom.NumberOfGuests}</span>
        </div>
        <div className="o-fixture-package__heading-group">
          <span>
            {packageRoom.ShowTBCDate ? labels.tbcDateText : packageRoom.Dates}
          </span>
        </div>
      </div>
      <div
        className="o-fixture-package__description ceva"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(packageRoom.Description),
        }}
      ></div>
      <div className="o-fixture-package__price-breakdown-mobile">
        <span>{labels.fromText}</span>
        <span>{renderPrice(Number(packageRoom.FromPrice))}</span> pp
        <span>{labels.vatText}</span>
      </div>
      <div className="o-fixture-package__footer o-fixture-package__footer--light">
        {!Boolean(selectOptions.length) || isSoldOutRoomOptions ? (
          <>
            <h4>{labels.soldOutText}</h4>
            <a
              className="o-fixture-package__action-btn"
              href={packageRoom.EnquireLink}
            >
              <span>{labels.enquireText}</span>
              <i className="zmdi zmdi-long-arrow-right"></i>
            </a>
          </>
        ) : (
          <>
            <div className="o-fixture-package__price-breakdown">
              <span>{labels.fromText}</span>
              <span>{renderPrice(Number(packageRoom.FromPrice))} pp</span>
              <span>{labels.vatText}</span>
            </div>
            <div className="o-fixture-package__action-wrapper">
              <span>{labels.numberOfGuestsText}</span>
              <Select
                options={selectOptions}
                value={optionValue}
                isSearchable={false}
                onChange={(option) => setOptionValue(option as TOption)}
              />
              <button
                className="o-fixture-package__action-btn o-fixture-package__action-btn--solid"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <span>{labels.optionsText}</span>
                {!isExpanded ? (
                  <i className="zmdi zmdi-chevron-down"></i>
                ) : (
                  <i className="zmdi zmdi-chevron-up"></i>
                )}
              </button>
            </div>
          </>
        )}
      </div>
      {isBasketSuccess && !isExpanded && (
        <div id="fixture-basket-success" className="o-fixture-package__options">
          {labels.basketSuccess}
        </div>
      )}
      {isExpanded && (
        <>
          <div
            className="o-fixture-package__options"
            style={{ position: "relative" }}
          >
            {isOptionsLoading ? (
              <Loader />
            ) : Boolean(packageOptions?.length) ? (
              <>
                <div className="o-fixture-package__options-row o-fixture-package__options-row--small">
                  <div>{labels.dateText}</div>
                  <div className="o-fixture-package__hide-mobile">
                    {labels.noOfGuestsShortText}
                  </div>
                  <div className="o-fixture-package__hide-mobile">
                    {labels.pricePerPersonText}
                  </div>
                  <div>{labels.quantityText}</div>
                </div>
                {packageOptions &&
                  packageOptions.map((item, index) => (
                    <div
                      className="o-fixture-package__options-row"
                      key={`${item.PackageId}-${index}`}
                    >
                      <div className="o-fixture-package__hide-desktop">
                        <div
                          style={
                            optionValue?.showTBCDate
                              ? { textAlign: "left" }
                              : {}
                          }
                        >
                          {optionValue?.showTBCDate
                            ? labels.tbcDateText
                            : item.FormattedDateString}
                        </div>
                        <div style={{ fontSize: "11px" }}>
                          <span
                            style={{
                              marginRight: "15px",
                              paddingRight: "15px",
                              borderRight: "1px solid #D7D7D7",
                            }}
                          >
                            {optionValue?.label}
                          </span>
                          <span>{renderPrice(item.DisplayPrice)}</span>
                        </div>
                      </div>
                      <div
                        className="o-fixture-package__hide-mobile"
                        style={
                          optionValue?.showTBCDate ? { textAlign: "left" } : {}
                        }
                      >
                        {optionValue?.showTBCDate
                          ? labels.tbcDateText
                          : item.FormattedDateString}
                      </div>
                      <div className="o-fixture-package__hide-mobile">
                        {optionValue?.label}
                      </div>
                      <div className="o-fixture-package__hide-mobile">
                        {renderPrice(item.DisplayPrice)}
                      </div>

                      {item.OutOfStock || isSoldOutPackage ? (
                        <a
                          className="o-fixture-package__action-btn"
                          href={packageRoom.EnquireLink}
                          style={{
                            color: "var(--primary-dark)",
                          }}
                        >
                          <span>{labels.enquireText}</span>
                          <i className="zmdi zmdi-long-arrow-right"></i>
                        </a>
                      ) : (
                        renderQuantity(item.Quantity, (newQuantity) =>
                          setPackageOptions(() => {
                            const newPackageOptions = [...packageOptions];

                            newPackageOptions.splice(index, 1, {
                              ...item,
                              Quantity: newQuantity,
                            });

                            return newPackageOptions;
                          })
                        )
                      )}
                    </div>
                  ))}
                {Boolean(optionalExtras?.length) && (
                  <div
                    className="o-fixture-package__options-row--small"
                    style={{ marginTop: "20px" }}
                  >
                    {labels.enhanceYourExperienceText}
                  </div>
                )}
                {optionalExtras &&
                  optionalExtras.map((item, index) => (
                    <div
                      className="o-fixture-package__options-row o-fixture-package__options-row--three"
                      key={`${item.PackageId}-${index}`}
                    >
                      <div>{item.OptionalExtraDescription}</div>
                      <div>
                        {renderPrice(
                          isVATExcludedPrice
                            ? item.PriceExcVat
                            : item.PriceIncVat
                        )}
                      </div>
                      {item.OutOfStock || isSoldOutPackage ? (
                        <a
                          className="o-fixture-package__action-btn"
                          href={packageRoom.EnquireLink}
                          style={{
                            color: "var(--primary-dark)",
                          }}
                        >
                          <span>{labels.enquireText}</span>
                          <i className="zmdi zmdi-long-arrow-right"></i>
                        </a>
                      ) : (
                        renderQuantity(item.Quantity, (newQuantity) =>
                          setOptionalExtras(() => {
                            const newPackageOptions = [...optionalExtras];

                            newPackageOptions.splice(index, 1, {
                              ...item,
                              Quantity: newQuantity,
                            });

                            return newPackageOptions;
                          })
                        )
                      )}
                    </div>
                  ))}
              </>
            ) : (
              labels.noPackageOptionsText
            )}
            {isBasketSubmitting && (
              <div
                style={{
                  position: "absolute",
                  top: "0",
                  left: "0",
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#0000001A",
                }}
              >
                <Loader />
              </div>
            )}
          </div>
          {isBasketError && (
            <span style={{ color: "red", padding: "0 20px 20px" }}>
              {labels.basketError}
            </span>
          )}
          {isBasketEmptyError && (
            <span style={{ color: "red", padding: "0 20px 20px" }}>
              {labels.addToBasketErrorEmpty}
            </span>
          )}
          {!isOptionsLoading && Boolean(packageOptions?.length) && (
            <div className="o-fixture-package__footer">
              <div className="o-fixture-package__price-breakdown total">
                <span>{labels.totalText}</span>
                <span>{renderPrice(totalPrice)}</span>
                <span>{labels.vatText}</span>
              </div>
              <button
                className="o-fixture-package__action-btn o-fixture-package__action-btn--rev"
                onClick={addToBasket}
                disabled={isBasketSubmitting}
              >
                <span>{labels.addToBasket}</span>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default PackageRoom;
